import React, { useState } from "react";
import { Pin, ExternalLink, X } from "lucide-react";
import { ShareButtons } from "./ShareButtons";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const photoSrc = (url) => (url || "").startsWith("/api/") ? `${BACKEND}${url}` : url;

export const fmtPostDate = (iso) => {
    try { return new Date(iso).toLocaleDateString("en-US", { weekday: "short", month: "long", day: "numeric", year: "numeric" }); }
    catch { return ""; }
};

// Plain text with preserved line breaks and clickable links
const Linkified = ({ text }) => {
    const parts = (text || "").split(/(https?:\/\/[^\s]+|www\.[^\s]+)/g);
    return (
        <p className="font-manrope text-[#3D2008] whitespace-pre-wrap break-words leading-relaxed">
            {parts.map((part, i) => /^(https?:\/\/|www\.)/.test(part)
                ? <a key={i} href={part.startsWith("http") ? part : `https://${part}`} target="_blank" rel="noopener noreferrer" className="text-[#C4522A] underline break-all">{part}</a>
                : part)}
        </p>
    );
};

const PhotoGrid = ({ photos, onOpen }) => {
    if (!photos?.length) return null;
    if (photos.length === 1) {
        return <button type="button" onClick={() => onOpen(0)} className="block w-full"><img src={photoSrc(photos[0])} alt="" loading="lazy" className="w-full max-h-[520px] object-cover border-2 border-[#3D2008]" /></button>;
    }
    const cols = photos.length === 2 || photos.length === 4 ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-3";
    return (
        <div className={`grid ${cols} gap-1.5`}>
            {photos.map((p, i) => (
                <button type="button" key={p + i} onClick={() => onOpen(i)} className="block aspect-square overflow-hidden border-2 border-[#3D2008]">
                    <img src={photoSrc(p)} alt="" loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
                </button>
            ))}
        </div>
    );
};

const Lightbox = ({ photos, index, onClose }) => {
    if (index === null) return null;
    return (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4" onClick={onClose} data-testid="post-lightbox">
            <button type="button" onClick={onClose} aria-label="Close" className="absolute top-4 right-4 w-11 h-11 flex items-center justify-center bg-white/10 text-white hover:bg-white/20"><X size={22} /></button>
            <img src={photoSrc(photos[index])} alt="" className="max-w-full max-h-full object-contain" onClick={e => e.stopPropagation()} />
        </div>
    );
};

export const PostCard = ({ post, index = 0, compact = false }) => {
    const [open, setOpen] = useState(null);
    const url = `${window.location.origin}/whats-happening#post-${post.id}`;
    return (
        <article id={`post-${post.id}`} data-testid={`post-${index}`}
            className="scroll-mt-28 bg-white border-2 border-[#3D2008] shadow-[6px_6px_0px_0px_rgba(61,32,8,0.2)]">
            <div className="flex items-center justify-between gap-3 px-5 pt-4">
                <div className="flex items-center gap-3">
                    <img src="/slsa_logo_transparent.png" alt="SLSA" className="w-9 h-9 object-contain bg-[#3D2008] p-1" />
                    <div>
                        <p className="font-anton text-[#3D2008] uppercase leading-none">SLSA</p>
                        <time className="font-manrope text-xs text-[#8A7A65]">{fmtPostDate(post.created_at)}</time>
                    </div>
                </div>
                {post.pinned && <span data-testid={`post-${index}-pinned`} className="inline-flex items-center gap-1 bg-[#C9A84C] text-[#3D2008] text-[11px] font-bold uppercase tracking-wide px-2 py-1"><Pin size={11} /> Pinned</span>}
            </div>
            <div className="px-5 py-4 space-y-4">
                {compact
                    ? <p className="font-manrope text-[#3D2008] leading-relaxed line-clamp-3 whitespace-pre-wrap">{post.text}</p>
                    : <Linkified text={post.text} />}
                {compact
                    ? post.photos?.[0] && <img src={photoSrc(post.photos[0])} alt="" loading="lazy" className="w-full h-44 object-cover border-2 border-[#3D2008]" />
                    : <PhotoGrid photos={post.photos} onOpen={setOpen} />}
                {post.link_url && !compact && (
                    <a href={post.link_url} target="_blank" rel="noopener noreferrer" data-testid={`post-${index}-link`}
                        className="inline-flex items-center gap-2 px-5 h-11 bg-[#C4522A] text-white font-manrope font-bold text-sm border-2 border-[#3D2008] hover:bg-[#A8431F] transition-colors">
                        {post.link_label || "Learn more"} <ExternalLink size={14} />
                    </a>
                )}
            </div>
            {!compact && (
                <div className="px-5 py-3 border-t border-[#E5D9BD] bg-[#F5EDD6]/60">
                    <ShareButtons compact label="" testId={`post-${index}-share`} url={url} title="Seeley Lake Skatepark Association" text={(post.text || "").slice(0, 140)} />
                </div>
            )}
            {!compact && <Lightbox photos={post.photos || []} index={open} onClose={() => setOpen(null)} />}
        </article>
    );
};
